import { useEffect } from 'react'
import { useAuthStore } from './auth.store'
import { getSessionContext, topesTipolocalizador, topesTratamientos } from '@core/api/sigma'
import {
  updateAppointment, updateAppointmentParams, getEtiquetasParams, datagetEtiquetas, getEtiquetas,
  addFollowUpParams, addFollowUp, getAllAppointment, getAllAppointmentParams, dataAllAppointment,
  getPolicies, dataPolicies, getPoliciesParams, getFollowUp, getFollowUpParams, dataGetFollowUp
} from "@/core/api/sigma"
import Swal from 'sweetalert2'
import { diagnosticosyTratamientosByPaisPRD, diagnosticosyTratamientosByPaisQA } from '@/shared/utils/diagnosticosyTratamientosByPais'
import { filterMatchingPolicies } from '@/shared/utils/policyUtils'
import { env } from '@/config/env';

/**
 * Hook para autenticación vía SSO Trusted Login (Caso 2).
 * El usuario llega con la cookie sso_sid seteada por el SSO después del redirect.
 * nlocaliza viene del context SSO (context.locator) en lugar de wstoken/verifySSO.
 */

interface filterPoliciesParams {
  ccedtitular: string;
  cciaseg: string;
  ccliente: string;
  cplan: string;
  cproducto: string;
  tlocaliza: string;
  cbeneficio: string;
}

export const useAuth = () => {
  // Solo destructuramos los setters. user se lee con useAuthStore.getState() cuando
  // se necesita el valor más reciente, así no nos suscribimos a re-renders del user.
  const { setUser, setAuthenticated, setLoading, setReasonCancellation, setTopes, resetUser } = useAuthStore()

  const stopExecutionDueToExistingSigns = () => {
    console.warn(`El localizador ya tiene una medida de signos vitales.`);

    setAuthenticated(false);
    setLoading(false);
    setReasonCancellation('VITAL_SIGNS_COMPLETED');
  };

  useEffect(() => {
    const timer = async () => {
      // Regla: solo /form, /vitalsscaning y /vitalsresults preservan el user
      // persistido (reload mid-flow). Cualquier otra ruta (vitalsintroduction,
      // páginas de error, root, etc.) implica "empezar de cero": se borran user,
      // sessionStorage.vitalsProfile y se carga fresco desde el SSO cookie.
      const MID_FLOW_PATHS = ['/form', '/vitalsscaning', '/vitalsresults']
      const isMidFlow = MID_FLOW_PATHS.includes(window.location.pathname)
      if (!isMidFlow) {
        resetUser()
        sessionStorage.removeItem('vitalsProfile')
      }
      setLoading(true)

      // Si el SSO redirigió con ?sso_error, manejar sin llamar al API
      const ssoError = new URLSearchParams(window.location.search).get('sso_error')
      if (ssoError) {
        
        //await handleAddFollowUp(error.data.cpais, error.data.xdato.nlocaliza, xmensaje)
        setReasonCancellation(ssoError)   // e.g. 'LINK_EXPIRED'
        setAuthenticated(false)
        setLoading(false)
        return
      }

      try {
        // Obtiene el contexto SSO — reemplaza verifySSO
        // nlocaliza = context.locator (enviado por teleconsulta al iniciar el flujo)
        const { user_id, context } = await getSessionContext()

        if (!user_id){
          setReasonCancellation('LINK_EXPIRED')
          setAuthenticated(false)
          setLoading(false)
          console.warn("No se obtuvo user_id del contexto SSO. Verificar configuración del SSO.")
          Swal.fire({
            icon: 'error',
            title: 'Error de autenticación',
            text: 'No se pudo obtener la información de sesión. Por favor, intenta nuevamente.',
          }) 
          return
        }

        console.log("Contexto SSO obtenido:", context)

        //caso en que no tiene cita ni localizador creado (generalmente deste enlinea)
        if(!context.locator){
          const inputPolicies: getPoliciesParams = {
            cpais: String(context.country), 
            ccedasegurado: context.ccedasegurado,
            publicacionplanestipoloc: "null",
            /*cciaseg: context.cciaseg, ccliente: context.ccliente,
            cplan: context.cplan, cproducto: context.cproducto,*/
          }

          const filtersPolicies : filterPoliciesParams = {
            ccedtitular:  context.ccedtitular,
            cciaseg: context.cciaseg,
            ccliente: context.ccliente,
            cplan: context.cplan,
            cproducto: context.cproducto,
            cbeneficio: context.cbeneficio,
            tlocaliza: "SV"//No viene de otra cita, es de signos vitales electivo
          }
          
          try{
            const policies = await validatePolicies(inputPolicies, filtersPolicies)

            if (!policies) {
              setReasonCancellation('PLAN_NOT_ACTIVE')
              setAuthenticated(false)
              setLoading(false)
              return
            }

            //***validar solo topes
            const responseTopesTipolocalizador = await topesTipolocalizador({
              cpais: String(context.country),
              cciaseg: context.cciaseg,
              ccliente: context.ccliente,
              cplan: context.cplan,
              ccedaseg: context.ccedasegurado,
              ctipoloc: "SV", //Signos Vitales
              //Las fechas deben tener formato dd/mm/yyyy
              fcita: new Date().toLocaleDateString('es-ES'), //fecha actual
              fdesde_pol: policies.policies[0].validfrom, //fecha inicio póliza
              fhasta_pol: policies.policies[0].validto, //fecha fin póliza
            })

            if(responseTopesTipolocalizador.supera_tope == 1){
              setReasonCancellation('PLAN_NOT_ACTIVE')
              setAuthenticated(false)
              setLoading(false)
              setTopes(responseTopesTipolocalizador)
              return
            }

            let cdiagnos = '', ctrata = '';
            if (env.isProduction){
                ({ cdiagnos, ctrata } = diagnosticosyTratamientosByPaisPRD[String(context.country)]);
            } else {
                ({ cdiagnos, ctrata } = diagnosticosyTratamientosByPaisQA[String(context.country)]);
            }

            const responseTopesTratamientos = await topesTratamientos({
              cpais: String(context.country),
              cciaseg: context.cciaseg,
              ccliente: context.ccliente,
              cplan: context.cplan,
              ccedaseg: context.ccedasegurado,
              fcita: new Date().toLocaleDateString('es-ES'), //fecha actual
              fdesde_pol: policies.policies[0].validfrom, //fecha inicio póliza
              fhasta_pol: policies.policies[0].validto, //fecha fin póliza
              cdiagnos: cdiagnos,
              ctrata: ctrata,
            })

            if(responseTopesTratamientos.supera_tope == 1){
              setReasonCancellation('PLAN_NOT_ACTIVE')
              setAuthenticated(false)
              setLoading(false)
              setTopes(responseTopesTratamientos)
              return
            }
            
            setTopes({ ...responseTopesTipolocalizador, ...responseTopesTratamientos })

            // Spread del user actual: en mid-flow conserva profile/results/nlocalizaCreated;
            // en /vitalsintroduction el resetUser de arriba lo dejó en initialUser, así que
            // los campos del flujo arrancan vacíos. No hay que pisarlos manualmente.
            setUser({
              ...useAuthStore.getState().user,
              cpais: context.country,
              ccedasegurado: context.ccedasegurado,
              coverage: policies,
              detailsRequest: {
                ccedasegurado: context.ccedasegurado,
                cciaseg: context.cciaseg,
                ccliente: context.ccliente,
                cplan: context.cplan,
                cproducto: context.cproducto,
                cprocedencia: context.cprocedencia,
                ccedtitular: context.ccedtitular,
                xcelular: context.xcelular,
                xemail: context.xemail,
              }
            })
          } catch (error) {
            console.error("Error obteniendo pólizas del usuario:", error)
            setReasonCancellation('PLAN_NOT_ACTIVE')
            setAuthenticated(false)
            setLoading(false)
            return
          }
        
        
        } else {

          //Trajo localizador del SSO, puede ser de correo SV, o de IS/OS (en caso de que el localizador venga de una cita de orientación inmediata o programada)

          let citaCreated : dataAllAppointment[];
          //validar si ya se hizo una medida de signos vitales para este loc
          let followUp = await handleGetFollowUp(String(context.country), String(context.locator), 'Escaneo exitoso de Signos Vitales');
          if (followUp?.length > 0) return stopExecutionDueToExistingSigns();

          followUp = await handleGetFollowUp(String(context.country), String(context.locator), 'Se creó localizador de Signos Vitales');
          if (followUp?.length > 0) {
            // Intentamos extraer el nuevo número de localizador del xdetalle
            const match = followUp[0].xdetalle.match(/No\.\s(\d+)/);
            
            if (match) {
              const subLocator = match[1];
              
              // Tercera validación: Escaneo exitoso en el sub-localizador encontrado
              const subFollowUp = await handleGetFollowUp(String(context.country), subLocator, 'Escaneo exitoso de Signos Vitales');
              if (subFollowUp?.length > 0) {
                return stopExecutionDueToExistingSigns();
              } else {
                const inputGetCitas: getAllAppointmentParams = {
                  cpais: String(context.country),
                  clocalizador: String(subLocator),
                  nregistro: "1",
                  detailLocator: "1",
                }

                try{
                  citaCreated  = await getAllAppointment(inputGetCitas)
                } catch (error) {
                  console.error("Error obteniendo cita del sublocalizador:", error)
                  Swal.fire({
                    icon: 'error',
                    title: 'Error del sistema',
                    text: 'tuvimos problemas realizando la consulta del localizador creado.',
                  });
                  setAuthenticated(false)
                  setLoading(false)
                  return
                }
              }
            }
          }

          console.log("Usuario con localizador, validando cita y pólizas asociadas...")
          //Caso en que ya tiene cita y localizador creado, por lo que se valida la cita y luego las pólizas asociadas a esa cita
          const inputGetCitas: getAllAppointmentParams = {
            cpais: String(context.country),
            clocalizador: String(context.locator),
            nregistro: "1",
            detailLocator: "1",
          }

          let cita: dataAllAppointment[] = []
          try{
            cita = await getAllAppointment(inputGetCitas)
          }catch (error) {
            console.error("Error obteniendo cita del sublocalizador:", error)
            Swal.fire({
              icon: 'error',
              title: 'Error del sistema',
              text: 'tuvimos problemas realizando la consulta del localizador creado.',
            });
            setAuthenticated(false)
            setLoading(false)
            return
          }

          if ((cita[0].iestado != "A" && cita[0].iestado != "Z") || cita[0].localizador.iestado == "N") {
            console.warn("Cita no cumple condiciones para la toma de Signos Vitales.")
            Swal.fire({
              icon: 'error',
              title: 'Condiciones de localizador',
              text: 'El localizador no cumple las condiciones para la toma de Signos Vitales.',
            }) 
            setAuthenticated(false)
            setLoading(false)

            //-Esto pasa en caso de correo, solo SV
            if(cita[0].localizador.tlocaliza == 'SV'){
              handleUpdateAppointmentStatus(cita[0].ccita, cita[0].ccedasegurado)
            }
            //handleUpdateAppointmentStatus(cita[0].ccita, cita[0].ccedasegurado)
            //No se debe actualizar el estado de la cita origen, porque no es la de signos.
            const xmensaje = (f, h) => `Localizador no cumple condiciones para la toma de Signos Vitales el ${f} a las ${h}`
            await handleAddFollowUp(cita[0].cpais, cita[0].clocalizador, xmensaje)
            return
          }else {
            console.log("Cita cumple condiciones para la toma de Signos Vitales.")
            console.log(cita[0]) // Log completo de la cita para depuración
          }

          let policiesForCoverage: dataPolicies | null = null;

          //-Cita de tipo IS/OS Orientación Inmediata y Orientación Programada
          if (cita[0].localizador.tlocaliza == "IS" || cita[0].localizador.tlocaliza == "OS") {

            console.log("Localizador de tipo IS/OS, validando pólizas asociadas a la cita...")
            const inputPolicies: getPoliciesParams = {
              cpais: String(cita[0].cpais), 
              ccedasegurado: cita[0].ccedasegurado,
              publicacionplanestipoloc: "null"
              /*cciaseg: cita[0].cciaseg, ccliente: cita[0].ccliente,
              cplan: cita[0].localizador.cplan, cproducto: cita[0].localizador.cproducto,*/
            }

            //-Datos de la Cita OS/IS para filtrar las pólizas. 
            const filtersPolicies : filterPoliciesParams = {
              ccedtitular:  cita[0].localizador.ccedtitu,
              cciaseg: cita[0].localizador.cciaseg,
              ccliente: cita[0].localizador.ccliente,
              cplan: cita[0].localizador.cplan,
              cproducto: cita[0].localizador.cproducto && cita[0].localizador.cproducto != "9999" && cita[0].localizador.cproducto != "999" ? cita[0].localizador.cproducto : null, //si el localizador no tiene producto asociado, no se filtra por producto
              cbeneficio: cita[0].localizador.cbeneficio && cita[0].localizador.cbeneficio != "9999" && cita[0].localizador.cproducto != "999"  ? cita[0].localizador.cbeneficio : null, //si el localizador no tiene beneficio asociado, no se filtra por beneficio
              tlocaliza: "SE"
            }

            const policies = await validatePolicies(inputPolicies, filtersPolicies)

            if (!policies) {
              console.warn("Pólizas del usuario no cumplen condiciones para la toma de Signos Vitales.")
              setReasonCancellation('PLAN_NOT_ACTIVE')
              setAuthenticated(false)
              setLoading(false)
              return
            }

             //***validar solo topes
            const responseTopesTipolocalizador = await topesTipolocalizador({
              cpais: String(cita[0].cpais),
              cciaseg: cita[0].localizador.cciaseg,
              ccliente: cita[0].localizador.ccliente,
              cplan: cita[0].localizador.cplan,
              ccedaseg: cita[0].localizador.ccedaseg,
              ctipoloc: "SE", //Signos Vitales Derviado de cita IS/OS
              //Las fechas deben tener formato dd/mm/yyyy
              fcita: new Date().toLocaleDateString('es-ES'), //fecha actual
              fdesde_pol: policies.policies[0].validfrom, //fecha inicio póliza
              fhasta_pol: policies.policies[0].validto, //fecha fin póliza
            })

            if(responseTopesTipolocalizador.supera_tope == 1){
              setReasonCancellation('PLAN_NOT_ACTIVE')
              setAuthenticated(false)
              setLoading(false)
              setTopes(responseTopesTipolocalizador)
              return
            }

            let cdiagnos = '', ctrata = '';
            if (env.isProduction){
                ({ cdiagnos, ctrata } = diagnosticosyTratamientosByPaisPRD[String(cita[0].cpais)]);
            } else {
                ({ cdiagnos, ctrata } = diagnosticosyTratamientosByPaisQA[String(cita[0].cpais)]);
            }

            const responseTopesTratamientos = await topesTratamientos({
              cpais: String(cita[0].cpais),
              cciaseg: cita[0].localizador.cciaseg,
              ccliente: cita[0].localizador.ccliente,
              cplan: cita[0].localizador.cplan,
              ccedaseg: cita[0].localizador.ccedaseg,
              fcita: new Date().toLocaleDateString('es-ES'), //fecha actual
              fdesde_pol: policies.policies[0].validfrom, //fecha inicio póliza
              fhasta_pol: policies.policies[0].validto, //fecha fin póliza
              cdiagnos: cdiagnos,
              ctrata: ctrata,
            })

            if(responseTopesTratamientos.supera_tope == 1){
              setReasonCancellation('PLAN_NOT_ACTIVE')
              setAuthenticated(false)
              setLoading(false)
              setTopes(responseTopesTratamientos)
              return
            }
            
            setTopes({ ...responseTopesTipolocalizador, ...responseTopesTratamientos })
            policiesForCoverage = policies

          }else {
            console.log("Localizador no es de tipo IS/OS, omitiendo validación de pólizas.")
          }
          
          // Spread del user actual: en mid-flow conserva profile/results y los Created
          // (puestos por /vitalsintroduction); en /vitalsintroduction el resetUser de
          // arriba ya los dejó vacíos. Solo pisamos los Created si el followUp lookup
          // encontró un loc previo (citaCreated); si no hay match, dejamos el spread —
          // que ya tiene el valor correcto para cada escenario.
          const updates: Record<string, unknown> = {
            cpais: cita[0].cpais,
            nlocaliza: String(context.locator),
            ccita: cita[0].ccita,
            ccedasegurado: cita[0].ccedasegurado,
            coverage: policiesForCoverage,
            detailLocalizador: cita[0].localizador,
          }
          if (citaCreated?.[0]?.localizador) {
            updates.nlocalizaCreated = citaCreated[0].localizador.nlocaliza
            updates.ccitaCreated = citaCreated[0].ccita
            updates.detailLocalizadorCreated = { ...citaCreated[0].localizador }
          }
          setUser({ ...useAuthStore.getState().user, ...updates })
        }
        console.log("Todo perfecto")
        setAuthenticated(true)
        
      } catch (error) {
          console.error("Error obteniendo cita del usuario:", error)
          console.error("Error obteniendo cita del usuario:", error.data?.imotivo)

        /*if (error.data?.imotivo) {
          setReasonCancellation(error.data.imotivo)
          setAuthenticated(false)
          setLoading(false)

          handleUpdateAppointmentStatus(error.data.xdato.ccita, error.data.xdato.ccedasegurado)
          const xmensaje = (f, h) => `Fuera del tiempo establecido para toma de Signos Vitales el ${f} a las ${h}`
          await handleAddFollowUp(error.data.cpais, error.data.xdato.nlocaliza, xmensaje)
          return
        }*/ //-Esto ya no funciona con imotivo, lo hace sso_error

        if (error.code === 'SESSION_EXPIRED') {
          setReasonCancellation('SESSION_EXPIRED')
        }

        setAuthenticated(false)

      } finally {
        setLoading(false)
      }
    }

    timer()
  }, [setUser, setAuthenticated, setLoading, setReasonCancellation, setTopes, resetUser])
}


const handleUpdateAppointmentStatus = async (ccita: string, ccedasegurado: string) => {
  try {
    const params: updateAppointmentParams = {
      ccita, ccedasegurado, iestado: 'Z'
    };
    await updateAppointment([params]);
  } catch (error) {
    console.error("Error actualizando estado de cita:", error);
  }
};

const handleAddFollowUp = async (cpais: string, nlocaliza: string, mensajeBuilder: (fecha: string, hora: string) => string) => {
  try {

    const pInputEtiquetas: getEtiquetasParams = {
      cpais, xdescripcion: 'información'
    }
    //Obtener la etiqueta necesaria
    const etiquetas: datagetEtiquetas[] = await getEtiquetas(pInputEtiquetas);

    if (!etiquetas.length) return;

    // Preparar el mensaje formateado
    const ahora = new Date();
    const fecha = ahora.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const hora = ahora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
    const mensaje = mensajeBuilder(fecha, hora);

    // Enviar el seguimiento
    const params: addFollowUpParams = {
      cpais, nlocaliza, xdetalle: mensaje,
      cetiqueta: etiquetas[0].cetiqueta,
      cusuario: '9999999999'
    };

    await addFollowUp([params]);
  } catch (error) {
    console.error("Error al añadir seguimiento:", error);
  }
};


const validatePolicies = async ( inputPolicies: getPoliciesParams, filters: filterPoliciesParams ): Promise<dataPolicies | null> => {
  try {
    const data: dataPolicies = await getPolicies(inputPolicies)

    const matchedPolicies = filterMatchingPolicies(data, filters.ccedtitular, filters.cciaseg, filters.ccliente, filters.cplan, filters.cproducto, filters.tlocaliza, filters.cbeneficio)

    if (!matchedPolicies.length) {
      console.warn("NO MATCH.")
      console.warn("Filters", filters)
      console.warn("Policies", data.policies)
      return null
    }

    console.warn("Policies", { ...data, policies: matchedPolicies })

    return { ...data, policies: matchedPolicies }
  } catch (error) {
    console.error("Error obteniendo pólizas del usuario:", error)
    return null
  }
}

const handleGetFollowUp = async (cpais: string, nlocaliza: string, xdetalle: string) => {
  try {
    const params: getFollowUpParams = {
      cpais, nlocaliza,
      xdetalle : xdetalle
    };

    const response: dataGetFollowUp[] = await getFollowUp(params);
    return response;
  } catch (error) {
    console.error("Error al consultar seguimiento:", error);
  }
};